<script setup lang="ts">
import { nextTick, reactive, ref, watch } from "vue";
import AgreementCheckbox from "@/components/AgreementCheckbox.vue";
import OrderSummary from "@/components/OrderSummary.vue";
import PromoCodeField from "@/components/PromoCodeField.vue";
import type {
  AppliedPromoCode,
  CheckoutForm,
  PaymentMethod,
  TicketOrderLine,
} from "@/types";

type CheckoutField = "lastName" | "firstName" | "phone" | "email";
type CheckoutFieldState = "neutral" | "success" | "error";

const props = defineProps<{
  form: CheckoutForm;
  paymentMethods: PaymentMethod[];
  selectedPaymentId: string;
  lines: TicketOrderLine[];
  totalPrice: number;
  discountAmount: number;
  formatPrice: (value: number) => string;
  isSubmitting: boolean;
  validatePromoCode: (code: string) => Promise<AppliedPromoCode>;
}>();

const emit = defineEmits<{
  selectPayment: [paymentId: string];
  promoChange: [promo: AppliedPromoCode | null];
  submit: [];
}>();

const touchedFields = reactive<Record<CheckoutField, boolean>>({
  lastName: false,
  firstName: false,
  phone: false,
  email: false,
});
const isPersonalAgreementTouched = ref(false);
const shakeNonce = ref(0);
const isShaking = ref(false);
const lastNameFieldRef = ref<HTMLElement | null>(null);
const firstNameFieldRef = ref<HTMLElement | null>(null);
const phoneFieldRef = ref<HTMLElement | null>(null);
const emailFieldRef = ref<HTMLElement | null>(null);
const personalAgreementFieldRef = ref<HTMLElement | null>(null);
let shakeTimer: ReturnType<typeof setTimeout> | null = null;

const emailErrorMessage = "Электронная почта введена неверно";
const phoneErrorMessage = "Номер телефона введен неверно";
const requiredErrorMessage = "Заполните поле";
const personalAgreementErrorMessage = "Подтвердите согласие";
const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/i;
const russianPhoneDigitsLimit = 11;
const russianPhoneFormattedLimit = 18;

const fieldInputClass =
  "h-[56px] lg:h-[60px] w-full rounded-full border bg-darkwhite px-5 lg:px-8 py-5 pr-11 lg:pr-14 text-sm lg:text-base leading-[18px] lg:leading-5 text-ink outline-none transition placeholder:text-ink/60 focus:border-brand/40";

watch(shakeNonce, (next, prev) => {
  if (!next || next === prev) return;
  isShaking.value = false;
  nextTick(() => {
    isShaking.value = true;
    if (shakeTimer) clearTimeout(shakeTimer);
    shakeTimer = setTimeout(() => {
      isShaking.value = false;
    }, 450);
  });
});

function getPhoneDigits(value: string) {
  return value.replace(/\D/g, "");
}

function getRussianCandidateDigits(raw: string, digits: string) {
  const trimmed = raw.trim();

  if (digits.startsWith("8")) return `7${digits.slice(1)}`;
  if (digits.startsWith("7")) return digits;
  if (!trimmed.startsWith("+") && digits.startsWith("9")) return `7${digits}`;

  return null;
}

function getRussianPhoneDigits(value: string) {
  const digits = getPhoneDigits(value);

  if (!digits) return null;

  return getRussianCandidateDigits(value, digits);
}

function formatRussianPhone(digits: string) {
  const body = digits.slice(1, 11);

  if (!body) return "+7";

  let value = `+7 (${body.slice(0, 3)}`;

  if (body.length >= 3) value += ")";
  if (body.length > 3) value += ` ${body.slice(3, 6)}`;
  if (body.length > 6) value += `-${body.slice(6, 8)}`;
  if (body.length > 8) value += `-${body.slice(8, 10)}`;

  return value;
}

function formatPhoneValue(raw: string) {
  const digits = getPhoneDigits(raw);

  if (!digits) return "";

  const russianDigits = getRussianCandidateDigits(raw, digits);

  if (russianDigits) {
    return formatRussianPhone(russianDigits.slice(0, russianPhoneDigitsLimit));
  }

  return `${raw.trim().startsWith("+") ? "+" : ""}${digits}`;
}

function isValidRussianPhone(value: string) {
  const digits = getRussianPhoneDigits(value);

  return !!digits && /^79\d{9}$/.test(digits);
}

function isFieldValid(field: CheckoutField) {
  switch (field) {
    case "lastName":
    case "firstName":
      return Boolean(props.form[field].trim());
    case "phone":
      return isValidRussianPhone(props.form.phone);
    case "email":
      return emailPattern.test(props.form.email.trim());
  }
}

function getFieldState(field: CheckoutField): CheckoutFieldState {
  if (isFieldValid(field)) return "success";
  if (touchedFields[field]) return "error";

  return "neutral";
}

function getFieldBorderClass(field: CheckoutField) {
  const state = getFieldState(field);

  if (state === "success") return "border-brand/40";
  if (state === "error") return "border-darkred/40";

  return "border-transparent";
}

function getFieldIcon(field: CheckoutField) {
  const state = getFieldState(field);

  if (state === "success") return "#ticket-widget-icon-check";
  if (state === "error" && hasFieldValue(field)) {
    return "#ticket-widget-icon-close";
  }

  return "";
}

function getFieldIconClass(field: CheckoutField) {
  return getFieldState(field) === "success" ? "text-brand" : "text-ink/70";
}

function getFieldError(field: CheckoutField) {
  if (getFieldState(field) !== "error") return "";

  if (field === "email") return emailErrorMessage;
  if (field === "phone") return phoneErrorMessage;

  return requiredErrorMessage;
}

function isFieldShaking(field: CheckoutField) {
  return isShaking.value && getFieldState(field) === "error";
}

function isPersonalAgreementInvalid() {
  return isPersonalAgreementTouched.value && !props.form.personalAgreement;
}

function getPersonalAgreementError() {
  return isPersonalAgreementInvalid() ? personalAgreementErrorMessage : "";
}

function touchPersonalAgreement() {
  isPersonalAgreementTouched.value = true;
}

function hasFieldValue(field: CheckoutField) {
  if (field === "phone") {
    return getPhoneDigits(props.form.phone).length > 1;
  }

  return Boolean(props.form[field].trim());
}

function touchField(field: CheckoutField) {
  touchedFields[field] = true;
}

function handlePhoneFocus() {
  if (!props.form.phone) {
    props.form.phone = "+7 ";
  }
}

function handlePhoneInput(event: Event) {
  const input = event.target as HTMLInputElement;
  const nextValue = formatPhoneValue(input.value);

  props.form.phone = nextValue;
  touchField("phone");

  nextTick(() => {
    input.setSelectionRange(nextValue.length, nextValue.length);
  });
}

function handlePhoneBlur() {
  touchField("phone");
  props.form.phone = formatPhoneValue(props.form.phone);

  if (props.form.phone === "+7") {
    props.form.phone = "";
  }
}

function validateForm() {
  (Object.keys(touchedFields) as CheckoutField[]).forEach(touchField);
  touchPersonalAgreement();

  const isValid =
    isFieldValid("lastName") &&
    isFieldValid("firstName") &&
    isFieldValid("phone") &&
    isFieldValid("email") &&
    props.form.personalAgreement;

  if (!isValid) {
    shakeNonce.value += 1;
  }

  return isValid;
}

async function scrollToFirstInvalidField() {
  await nextTick();

  let target: HTMLElement | null = null;

  if (!isFieldValid("lastName")) target = lastNameFieldRef.value;
  else if (!isFieldValid("firstName")) target = firstNameFieldRef.value;
  else if (!isFieldValid("phone")) target = phoneFieldRef.value;
  else if (!isFieldValid("email")) target = emailFieldRef.value;
  else if (!props.form.personalAgreement) {
    target = personalAgreementFieldRef.value;
  }

  if (!target) return;

  target.scrollIntoView({
    behavior: "smooth",
    block: "center",
  });

  const focusTarget = target.querySelector<HTMLElement>(
    "input, button, [href], [tabindex]:not([tabindex='-1'])",
  );
  focusTarget?.focus({ preventScroll: true });
}

function handleSubmit() {
  if (props.isSubmitting) return;

  if (!validateForm()) {
    void scrollToFirstInvalidField();
    return;
  }

  emit("submit");
}

defineExpose({
  submit: handleSubmit,
});
</script>

<template>
  <section
    class="grid w-full items-start gap-6 xl:grid-cols-[minmax(0,1fr)_500px] xl:gap-5 [&>*]:min-w-0"
  >
    <section
      class="rounded-[24px] lg:rounded-[40px] bg-white p-4 lg:p-8 shadow-widget"
    >
      <h2 class="m-0 text-base lg:text-2xl font-medium text-ink">
        Введите данные для получения билета
      </h2>

      <div
        class="mt-4 lg:mt-6 grid gap-y-3 lg:gap-y-8 lg:gap-x-4 lg:grid-cols-2"
      >
        <div
          ref="lastNameFieldRef"
          :class="{ 'animate-shake': isFieldShaking('lastName') }"
        >
          <div class="relative">
            <input
              v-model="form.lastName"
              :class="[fieldInputClass, getFieldBorderClass('lastName')]"
              placeholder="Фамилия"
              type="text"
              :aria-invalid="getFieldState('lastName') === 'error'"
              autocomplete="family-name"
              @input="touchField('lastName')"
              @blur="touchField('lastName')"
            />
            <svg
              v-if="getFieldIcon('lastName')"
              class="pointer-events-none h-6 w-6 min-w-6 top-1/2 transform-[translateY(-50%)] right-8 absolute"
              :class="getFieldIconClass('lastName')"
              aria-hidden="true"
            >
              <use :href="getFieldIcon('lastName')" />
            </svg>
          </div>
          <p
            v-if="getFieldError('lastName')"
            class="mt-0.5 lg:mt-1 px-3 text-[10px] lg:text-sm leading-3.5 lg:leading-4 text-darkred"
          >
            {{ getFieldError("lastName") }}
          </p>
        </div>

        <div
          ref="firstNameFieldRef"
          :class="{ 'animate-shake': isFieldShaking('firstName') }"
        >
          <div class="relative">
            <input
              v-model="form.firstName"
              :class="[fieldInputClass, getFieldBorderClass('firstName')]"
              placeholder="Имя"
              type="text"
              :aria-invalid="getFieldState('firstName') === 'error'"
              autocomplete="given-name"
              @input="touchField('firstName')"
              @blur="touchField('firstName')"
            />
            <svg
              v-if="getFieldIcon('firstName')"
              class="pointer-events-none h-6 w-6 min-w-6 top-1/2 transform-[translateY(-50%)] right-8 absolute"
              :class="getFieldIconClass('firstName')"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <use :href="getFieldIcon('firstName')" />
            </svg>
          </div>
          <p
            v-if="getFieldError('firstName')"
            class="mt-0.5 lg:mt-1 px-3 text-[10px] lg:text-sm leading-3.5 lg:leading-4 text-darkred"
          >
            {{ getFieldError("firstName") }}
          </p>
        </div>

        <div
          ref="phoneFieldRef"
          :class="{ 'animate-shake': isFieldShaking('phone') }"
        >
          <div class="relative">
            <input
              :value="form.phone"
              :class="[fieldInputClass, getFieldBorderClass('phone')]"
              placeholder="+7 (XXX) XXX-XX-XX"
              type="tel"
              :maxlength="russianPhoneFormattedLimit"
              :aria-invalid="getFieldState('phone') === 'error'"
              inputmode="tel"
              autocomplete="tel"
              @focus="handlePhoneFocus"
              @input="handlePhoneInput"
              @blur="handlePhoneBlur"
            />
            <svg
              v-if="getFieldIcon('phone')"
              class="pointer-events-none h-6 w-6 min-w-6 top-1/2 transform-[translateY(-50%)] right-8 absolute"
              :class="getFieldIconClass('phone')"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <use :href="getFieldIcon('phone')" />
            </svg>
          </div>
          <p
            v-if="getFieldError('phone')"
            class="mt-0.5 lg:mt-1 px-3 text-[10px] lg:text-sm leading-3.5 lg:leading-4 text-darkred"
          >
            {{ getFieldError("phone") }}
          </p>
        </div>

        <div
          ref="emailFieldRef"
          :class="{ 'animate-shake': isFieldShaking('email') }"
        >
          <div class="relative">
            <input
              v-model="form.email"
              :class="[fieldInputClass, getFieldBorderClass('email')]"
              placeholder="Почта"
              type="email"
              :aria-invalid="getFieldState('email') === 'error'"
              autocomplete="email"
              @input="touchField('email')"
              @blur="touchField('email')"
            />
            <svg
              v-if="getFieldIcon('email')"
              class="pointer-events-none h-6 w-6 min-w-6 top-1/2 transform-[translateY(-50%)] right-8 absolute"
              :class="getFieldIconClass('email')"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <use :href="getFieldIcon('email')" />
            </svg>
          </div>
          <p
            v-if="getFieldError('email')"
            class="mt-0.5 lg:mt-1 px-3 text-[10px] lg:text-sm leading-3.5 lg:leading-4 text-darkred"
          >
            {{ getFieldError("email") }}
          </p>
        </div>
      </div>

      <div class="mt-4 lg:mt-7 grid gap-3">
        <div ref="personalAgreementFieldRef">
          <AgreementCheckbox
            v-model="form.personalAgreement"
            :is-error="isPersonalAgreementInvalid()"
            :shake-nonce="isPersonalAgreementInvalid() ? shakeNonce : 0"
            @update:model-value="touchPersonalAgreement"
          >
            Я согласен на
            <a href="#" target="_blank" class="text-brand">
              обработку персональных данных
            </a>
          </AgreementCheckbox>
          <p
            v-if="getPersonalAgreementError()"
            class="mt-0.5 lg:mt-1 px-7 text-[10px] lg:text-sm leading-3.5 lg:leading-4 text-darkred"
          >
            {{ getPersonalAgreementError() }}
          </p>
        </div>

        <AgreementCheckbox v-model="form.marketingAgreement">
          Я согласен на
          <a href="#" class="text-brand" target="_blank">
            получение информации о скидках и акциях</a
          >, в том числе от Продавца и Организатора
        </AgreementCheckbox>
      </div>

      <div class="my-7 h-px bg-ink/10" />

      <h2 class="m-0 text-base lg:text-2xl font-medium text-ink">
        Выберите способ оплаты
      </h2>

      <div
        class="mt-6 overflow-hidden rounded-[20px] bg-darkwhite py-4 px-3 lg:py-5 lg:px-5"
      >
        <label
          v-for="method in paymentMethods"
          :key="method.id"
          class="flex w-full cursor-pointer items-center justify-between border-b border-ink/10 last:border-b-0 pb-5 last:pb-0 pt-5 first:pt-0"
        >
          <span class="flex items-center gap-3">
            <input
              class="sr-only"
              name="payment-method"
              type="radio"
              :value="method.id"
              :checked="method.id === selectedPaymentId"
              @change="emit('selectPayment', method.id)"
            />
            <span
              class="grid h-5 w-5 shrink-0 place-items-center rounded-full border-[2px]"
              :class="
                method.id === selectedPaymentId
                  ? 'border-brand bg-brand'
                  : 'border-ink/30'
              "
            >
              <span
                v-if="method.id === selectedPaymentId"
                class="h-2 w-2 rounded-full bg-white"
              />
            </span>
            <span class="min-w-0 text-lg font-semibold text-ink">
              {{ method.title }}
            </span>
          </span>
          <span v-if="method.icon" class="text-ink" aria-hidden="true">
            <svg :class="method.style">
              <use :href="method.icon" />
            </svg>
          </span>
        </label>
      </div>

      <PromoCodeField
        :validate-promo-code="validatePromoCode"
        @promo-change="emit('promoChange', $event)"
      />
    </section>

    <OrderSummary
      class="hidden xl:block"
      :lines="lines"
      :total-price="totalPrice"
      :discount-amount="discountAmount"
      :format-price="formatPrice"
      action-label="Оплатить"
      loading-label="Оплачиваем..."
      :is-loading="isSubmitting"
      compact
      @action="handleSubmit"
    />
  </section>
</template>
